// Shared schemas. Keep in sync with src/marine_detect/{detection,events,config}.py.
// If you change Detection/Event shape here, change it in Python too — the JSONL
// is meant to be round-trip compatible across both implementations.

export interface Bbox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface Detection {
  cls_id: number;
  cls_name: string;
  confidence: number; // 0-1
  bbox: Bbox; // pixel coords in source frame
  track_id: number | null;
  seen_for_seconds: number | null;
}

export type Severity = "info" | "warning" | "critical";

export interface AlertEvent {
  event_id: string; // "evt_001"
  type: string; // "forward_zone_object"
  severity: Severity;
  track_id: number | null;
  cls_name: string; // serialized as "class" in JSONL — see toJsonRecord()
  confidence: number;
  seen_for_seconds: number;
  reason: string;
  timestamp: string; // ISO 8601 UTC, "...Z"
  snapshot_path?: string;
}

/**
 * Convert an AlertEvent to the on-the-wire shape (uses "class" not "cls_name"),
 * matching Python's events.Event.to_json().
 */
export function eventToJsonRecord(ev: AlertEvent): Record<string, unknown> {
  const out: Record<string, unknown> = {
    event_id: ev.event_id,
    type: ev.type,
    severity: ev.severity,
    track_id: ev.track_id,
    class: ev.cls_name,
    confidence: round(ev.confidence, 4),
    seen_for_seconds: round(ev.seen_for_seconds, 2),
    reason: ev.reason,
    timestamp: ev.timestamp,
  };
  if (ev.snapshot_path !== undefined) out.snapshot_path = ev.snapshot_path;
  return out;
}

function round(n: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(n * factor) / factor;
}

export type DevicePreference = "webgpu" | "wasm";

export type StorageMode = "indexeddb" | "memory" | "off";

export interface PolygonPoint {
  x: number; // 0-1, normalized
  y: number; // 0-1, normalized
}

export interface AppConfig {
  // Detection
  confidence_threshold: number; // 0-1
  classes: string[] | null; // null = all classes

  // Tracking
  track_grace_seconds: number;

  // Danger zone + alerts
  danger_zone_enabled: boolean;
  alert_on_danger_zone: boolean;
  min_persistence_seconds: number;
  alert_cooldown_seconds: number;
  min_alert_confidence: number;
  danger_zone_polygon: PolygonPoint[]; // normalized 0-1

  // Runtime
  inference_device: DevicePreference;
  capture_fps: number;

  // Output
  storage_mode: StorageMode;

  // Webhook (POST each event to an external API). Demo feature — credentials
  // live in this browser's localStorage and travel out-of-band over HTTPS.
  webhook_enabled: boolean;
  webhook_url: string;
  webhook_api_key: string;

  // Display
  show_hud: boolean;
  show_track_ids: boolean;
  show_confidence: boolean;
}

export const DEFAULT_CONFIG: AppConfig = {
  confidence_threshold: 0.5,
  classes: null,
  track_grace_seconds: 2.0,
  danger_zone_enabled: true,
  alert_on_danger_zone: true,
  min_persistence_seconds: 5.0,
  alert_cooldown_seconds: 10.0,
  min_alert_confidence: 0.5,
  // Default forward trapezoid — tighter than the polygon we shipped first time
  // (which covered the full lower half). This one lands in the lower-middle
  // and reads as "the area in front of and just below the camera". Users can
  // clear it and draw their own.
  danger_zone_polygon: [
    { x: 0.4, y: 0.6 },
    { x: 0.6, y: 0.6 },
    { x: 0.78, y: 0.92 },
    { x: 0.22, y: 0.92 },
  ],
  inference_device: "webgpu",
  capture_fps: 20,
  storage_mode: "indexeddb",
  // Default-on for the demo: out of the box, events POST to the Sealog
  // fallback endpoint (see web/src/runtime/webhook.ts). Users with their
  // own backend can override URL/key in the sidebar.
  webhook_enabled: true,
  webhook_url: "",
  webhook_api_key: "",
  show_hud: true,
  show_track_ids: true,
  show_confidence: true,
};

/** COCO class names, indexed by class id. Matches what yolo11n was trained on. */
export const COCO_CLASSES: readonly string[] = [
  "person", "bicycle", "car", "motorcycle", "airplane", "bus", "train", "truck",
  "boat", "traffic light", "fire hydrant", "stop sign", "parking meter", "bench",
  "bird", "cat", "dog", "horse", "sheep", "cow", "elephant", "bear", "zebra",
  "giraffe", "backpack", "umbrella", "handbag", "tie", "suitcase", "frisbee",
  "skis", "snowboard", "sports ball", "kite", "baseball bat", "baseball glove",
  "skateboard", "surfboard", "tennis racket", "bottle", "wine glass", "cup",
  "fork", "knife", "spoon", "bowl", "banana", "apple", "sandwich", "orange",
  "broccoli", "carrot", "hot dog", "pizza", "donut", "cake", "chair", "couch",
  "potted plant", "bed", "dining table", "toilet", "tv", "laptop", "mouse",
  "remote", "keyboard", "cell phone", "microwave", "oven", "toaster", "sink",
  "refrigerator", "book", "clock", "vase", "scissors", "teddy bear", "hair drier",
  "toothbrush",
] as const;
